import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  KeyRound,
  Loader2,
  Mail,
  MessageCircle,
  Moon,
  Phone,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import api from '../../utils/api';
import useTheme from '../../hooks/useTheme';
import './LoginPage.css';
import AnimatedBrandText from '../home/AnimatedBrandText';
import { completeLogin, safeReturnTarget } from './authSession';
import {
  confirmPasswordReset,
  createWechatLoginSession,
  pollWechatLoginSession,
  sendPasswordResetCode,
} from './wechatAuthApi';
import WechatQrFlow from './WechatQrFlow';
import XiaoyingMark from '../components/XiaoyingMark';
import '../components/XiaoyingMark.css';
import PublicTeamBrandProvider from '../branding/PublicTeamBrandProvider';
import { BrandText } from '../branding/TeamBrandContext';
import { joinTeamWithCurrentSession, teamLoginContext } from './teamLogin';

function initialMethod() {
  if (typeof window === 'undefined') return 'wechat';
  return window.matchMedia?.('(max-width: 639px)')?.matches ? 'phone' : 'wechat';
}

// 没勾协议时那句提示。抽成常量是为了勾上之后能精确地把它收回去——
// 用字面量比对的话，改文案时另一处会忘记跟着改。
const AGREEMENT_NOTICE = '请先勾选下方的《用户协议》和《隐私政策》，再点登录。';

export default function LoginPage() {
  const [method, setMethod] = useState(initialMethod);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  // 提示过一次之后把勾选框高亮出来，让用户一眼看到该点哪儿。
  const [agreementHint, setAgreementHint] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [notice, setNotice] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const countdownTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const teamContext = useMemo(() => teamLoginContext(location.search), [location.search]);
  const hasTeamTarget = Object.hasOwn(teamContext, 'login_team_id');
  const [joiningTeam, setJoiningTeam] = useState(false);
  const [joinFailed, setJoinFailed] = useState(false);
  const joinAttemptRef = useRef(0);
  const { theme, setTheme } = useTheme();
  const darkActive = theme === 'dark'
    || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
  const returnTarget = useMemo(
    () => hasTeamTarget ? '/home' : safeReturnTarget(location.state?.from),
    [hasTeamTarget, location.state],
  );
  const cleanPhone = phone.replace(/\D/g, '').slice(0, 11);
  const phoneValid = /^1\d{10}$/.test(cleanPhone);
  const codeValid = /^\d{4,6}$/.test(code) || /^\d{12,18}$/.test(code);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const onWechatAuthorized = useCallback(result => {
    completeLogin({
      token: result.token,
      user: result.user,
      navigate,
      returnTarget,
    });
  }, [navigate, returnTarget]);
  const createLoginSession = useCallback(() => createWechatLoginSession(teamContext), [teamContext]);
  const joinCurrentTeam = useCallback(async () => {
    const attempt = ++joinAttemptRef.current;
    const token = localStorage.getItem('token');
    setJoinFailed(false);
    if (!hasTeamTarget || !token) { setJoiningTeam(false); return; }
    const isCurrent = () => attempt === joinAttemptRef.current && token === localStorage.getItem('token');
    setJoiningTeam(true);
    setErrorMsg('');
    try {
      const result = await joinTeamWithCurrentSession(teamContext);
      if (!isCurrent()) return;
      completeLogin({ token, user: result.user, navigate, returnTarget });
    } catch (error) {
      if (!isCurrent()) return;
      if (error?.response?.status === 401) {
        localStorage.removeItem('token');
        setNotice('登录状态已过期，请登录后加入团队。');
      } else {
        setJoinFailed(true);
        setErrorMsg(getErrorMessage(error, '加入团队失败，请稍后重试'));
      }
    } finally {
      if (attempt === joinAttemptRef.current) setJoiningTeam(false);
    }
  }, [hasTeamTarget, navigate, returnTarget, teamContext]);
  useEffect(() => {
    joinCurrentTeam();
    return () => { joinAttemptRef.current += 1; };
  }, [joinCurrentTeam]);

  const chooseMethod = value => {
    setMethod(value);
    if (value !== 'password') setResetMode(false);
    setNotice('');
    setErrorMsg('');
    setCountdown(0);
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const openPasswordReset = () => {
    setResetMode(true);
    setResetCodeSent(false);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setNotice('');
    setErrorMsg('');
    setCountdown(0);
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const closePasswordReset = () => {
    setResetMode(false);
    setResetCodeSent(false);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setNotice('');
    setErrorMsg('');
    setCountdown(0);
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const refreshWechatCode = () => {
    setNotice(AGREEMENT_NOTICE);
    setAgreementHint(true);
  };

  const startCountdown = () => {
    if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
    setCountdown(60);
    countdownTimerRef.current = window.setInterval(() => {
      setCountdown(value => {
        if (value <= 1) {
          window.clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          return 0;
        }
        return value - 1;
      });
    }, 1000);
  };

  useEffect(() => () => {
    if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
  }, []);

  const sendCode = async () => {
    if (!phoneValid || sending || countdown > 0 || joiningTeam) return;
    setSending(true);
    setNotice('');
    setErrorMsg('');
    try {
      const res = await api.post('/auth/sms/send', { phone: `+86${cleanPhone}` });
      startCountdown();
      if (res.data?.dev_code) {
        setCode(res.data.dev_code);
        setNotice(`[开发模式] 验证码 ${res.data.dev_code} 已自动填入`);
      } else {
        setNotice('验证码已发送，请留意短信。');
      }
    } catch (error) {
      setErrorMsg(getErrorMessage(error, '验证码发送失败，请稍后重试。'));
    } finally {
      setSending(false);
    }
  };

  const verifyPhone = async event => {
    event.preventDefault();
    if (!phoneValid || !codeValid || verifying || joiningTeam) return;
    if (!agreed) { setNotice(AGREEMENT_NOTICE); setAgreementHint(true); return; }
    setVerifying(true);
    setNotice('');
    setErrorMsg('');
    try {
      const response = await api.post('/auth/sms/verify', {
        phone: `+86${cleanPhone}`,
        code,
        ...teamContext,
      });
      completeLogin({ token: response.data.token, user: response.data.user, navigate, returnTarget });
    } catch (error) {
      setErrorMsg(getErrorMessage(error, '验证码无效或已过期，请重新获取。'));
    } finally {
      setVerifying(false);
    }
  };

  const submitPassword = async event => {
    event.preventDefault();
    if (!emailValid || !password || verifying || joiningTeam) return;
    if (!agreed) { setNotice(AGREEMENT_NOTICE); setAgreementHint(true); return; }
    setVerifying(true);
    setNotice('');
    setErrorMsg('');
    try {
      const response = await api.post('/auth/login', { email: email.trim(), password, ...teamContext });
      completeLogin({ token: response.data.token, user: response.data.user, navigate, returnTarget });
    } catch (error) {
      setErrorMsg(getErrorMessage(error, '邮箱或密码不正确。'));
    } finally {
      setVerifying(false);
    }
  };

  const sendResetCode = async () => {
    if (!emailValid || sending || countdown > 0) return;
    setSending(true);
    setNotice('');
    setErrorMsg('');
    try {
      const result = await sendPasswordResetCode(email.trim());
      setResetCodeSent(true);
      startCountdown();
      setNotice(`验证码已发送至 ${result.email || '你的邮箱'}，请在 ${Math.ceil(Number(result.expires_in_seconds || 300) / 60)} 分钟内完成重置。`);
    } catch (error) {
      setErrorMsg(getErrorMessage(error, '验证码发送失败，请稍后重试。'));
    } finally {
      setSending(false);
    }
  };

  const submitPasswordReset = async event => {
    event.preventDefault();
    if (!resetCodeSent) {
      await sendResetCode();
      return;
    }
    if (!/^\d{6}$/.test(resetCode) || newPassword.length < 8 || newPassword !== confirmPassword || verifying) return;
    setVerifying(true);
    setNotice('');
    setErrorMsg('');
    try {
      await confirmPasswordReset({
        email: email.trim(),
        code: resetCode,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      closePasswordReset();
      setPassword('');
      setNotice('密码已重置，所有旧设备已退出。请使用新密码登录。');
    } catch (error) {
      setErrorMsg(getErrorMessage(error, '密码重置失败，请检查验证码后重试。'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <PublicTeamBrandProvider>
    <main className="vnext-auth relative flex min-h-screen items-center justify-center overflow-x-hidden overflow-y-auto px-4 py-8 sm:px-8">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="vnext-auth__glow vnext-auth__glow--lavender absolute left-[8%] top-[14%] h-72 w-72 rounded-full blur-[120px]" />
        <div className="vnext-auth__glow vnext-auth__glow--sage absolute bottom-[4%] right-[8%] h-80 w-80 rounded-full blur-[140px]" />
      </div>

      <Link
        to="/home"
        className="vnext-auth__floating-control absolute left-5 top-5 z-20 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px] backdrop-blur-xl transition sm:left-8 sm:top-8"
      >
        <ArrowLeft size={14} />
        返回首页
      </Link>

      <button
        type="button"
        onClick={() => setTheme(darkActive ? 'light' : 'dark')}
        className="vnext-auth__floating-control absolute right-5 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-xl transition sm:right-8 sm:top-8"
        aria-label={darkActive ? '切换到浅色模式' : '切换到深色模式'}
        title={darkActive ? '切换到浅色模式' : '切换到深色模式'}
      >
        {darkActive ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      <section className="vnext-auth__card relative my-auto grid w-full max-w-[1040px] overflow-hidden rounded-[30px] lg:grid-cols-[0.92fr_1.08fr]">
        <div className="flex min-h-[650px] flex-col px-7 py-8 sm:px-12 sm:py-11 lg:min-h-[680px]">
          {/* 品牌用炫光字标，不再是那个四格图案 + 小影 + XIAOYING STUDIO。
              复用首页那个 AnimatedBrandText，不另起一套——字标出现在
              首页、画布 Agent、加载态和这里，各写一份迟早漂成四个样子。 */}
          <header className="flex items-center gap-2.5">
            <XiaoyingMark size={34} />
            <h2 className="text-[23px] font-semibold tracking-[-0.03em]">
              <AnimatedBrandText />
              <span className="sr-only"><BrandText>小影</BrandText></span>
            </h2>
          </header>

          <div className="mt-12">
            <p className="vnext-auth__faint text-[10px] font-semibold uppercase tracking-[0.2em]">Continue your story</p>
            <h1 className="mt-3 text-[30px] font-semibold tracking-[-0.045em]">登录后继续创作</h1>
            <p className="vnext-auth__muted mt-3 max-w-sm text-[13px] leading-6">项目、画布和资产会安全同步到你的账号。</p>
            {hasTeamTarget && <p className="vnext-auth__muted mt-3 text-[12px] leading-6" role="status">{joiningTeam ? '正在加入团队…' : '登录后自动加入此团队，不会另建默认团队。已有成员将直接切换到此团队。'}</p>}
            {joinFailed && <button type="button" className="vnext-auth__qr-retry mt-3 rounded-full px-4 py-2 text-[11px]" onClick={joinCurrentTeam} disabled={joiningTeam}>{joiningTeam ? '加入中…' : '重试加入团队'}</button>}
          </div>

          <div className="vnext-auth__tabs mt-8 grid grid-cols-3 rounded-[14px] p-1" role="tablist" aria-label="登录方式">
            {[
              { value: 'wechat', label: '微信登录', icon: MessageCircle },
              { value: 'phone', label: '手机号', icon: Phone },
              { value: 'password', label: '邮箱', icon: Mail },
            ].map(option => {
              const Icon = option.icon;
              const active = method === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={joiningTeam}
                  onClick={() => chooseMethod(option.value)}
                  className={`vnext-auth__tab flex h-10 items-center justify-center gap-2 rounded-[10px] text-[12px] font-medium transition ${
                    active ? 'vnext-auth__tab--active' : ''
                  } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a792bd]/55`}
                >
                  <Icon size={14} strokeWidth={1.8} />
                  {option.label}
                </button>
              );
            })}
          </div>

          {method === 'password' && resetMode ? (
            <form onSubmit={submitPasswordReset} className="mt-7 flex flex-1 flex-col">
              <div className="vnext-auth__reset-heading">
                <span className="vnext-auth__reset-icon"><KeyRound size={16} /></span>
                <div><strong>找回登录密码</strong><small>{resetCodeSent ? '输入邮件中的验证码并设置新密码' : '先验证注册邮箱，再设置新密码'}</small></div>
                <button type="button" onClick={closePasswordReset}>返回登录</button>
              </div>

              <label className="vnext-auth__label mt-6 text-[11px] font-medium" htmlFor="reset-email">注册邮箱</label>
              <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                <input id="reset-email" type="email" autoComplete="username" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@example.com" className="vnext-auth__input h-full flex-1 bg-transparent px-4 text-[13px] outline-none" />
              </div>

              {resetCodeSent && <>
                <label className="vnext-auth__label mt-4 text-[11px] font-medium" htmlFor="reset-code">邮件验证码</label>
                <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                  <input id="reset-code" inputMode="numeric" autoComplete="one-time-code" value={resetCode} onChange={event => setResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="六位验证码" className="vnext-auth__input h-full min-w-0 flex-1 bg-transparent px-4 text-[13px] outline-none" />
                  <button type="button" className="vnext-auth__send mr-2 rounded-[9px] px-2.5 py-2 text-[10px]" onClick={sendResetCode} disabled={sending || countdown > 0}>{sending ? '发送中…' : countdown > 0 ? `${countdown}s` : '重新发送'}</button>
                </div>

                <div className="vnext-auth__reset-passwords mt-4">
                  <label><span>新密码</span><div className="vnext-auth__field"><input type="password" autoComplete="new-password" value={newPassword} onChange={event => setNewPassword(event.target.value)} placeholder="至少 8 个字符" /></div></label>
                  <label><span>确认密码</span><div className="vnext-auth__field"><input type="password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} placeholder="再次输入新密码" /></div></label>
                </div>
                {confirmPassword && newPassword !== confirmPassword && <p className="vnext-auth__field-hint is-error" role="alert">两次输入的密码不一致</p>}
              </>}

              <button type="submit" disabled={!emailValid || sending || verifying || (resetCodeSent && (!/^\d{6}$/.test(resetCode) || newPassword.length < 8 || newPassword !== confirmPassword))} className="vnext-auth__submit mt-auto flex h-11 items-center justify-center gap-2 rounded-[11px] text-[12px] font-semibold transition disabled:cursor-not-allowed">
                {sending || verifying ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {resetCodeSent ? (verifying ? '正在重置…' : '重置密码') : (sending ? '正在发送…' : '发送验证码')}
              </button>
            </form>
          ) : method === 'password' ? (
            <form onSubmit={submitPassword} className="mt-7 flex flex-1 flex-col">
              <div className="vnext-auth__email-heading">
                <div><strong>邮箱登录</strong><span><BrandText>首次使用小影？注册后会自动回到当前页面。</BrandText></span></div>
                <Link to={`/register${location.search || ''}`} state={{ from: returnTarget }}>创建账号 <ArrowRight size={12} /></Link>
              </div>
              <label className="vnext-auth__label text-[11px] font-medium" htmlFor="login-email">邮箱</label>
              <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="vnext-auth__input h-full flex-1 bg-transparent px-4 text-[13px] outline-none"
                />
              </div>

              <div className="vnext-auth__label-row mt-5"><label className="vnext-auth__label text-[11px] font-medium" htmlFor="login-password">密码</label><button type="button" onClick={openPasswordReset}>忘记密码？</button></div>
              <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="请输入密码"
                  className="vnext-auth__input h-full flex-1 bg-transparent px-4 text-[13px] outline-none"
                />
              </div>

              <p className="vnext-auth__muted mt-4 text-[11px] leading-5">登录后继续同步项目、画布和点数记录。</p>

              <div className="mt-auto pt-6">
                <button
                  type="submit"
                  disabled={!emailValid || !password || verifying || joiningTeam}
                  className="vnext-auth__submit flex h-12 w-full items-center justify-center gap-2 rounded-[13px] text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-55"
                >
                  {verifying ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                  {verifying ? '登录中…' : '登录'}
                </button>
              </div>
            </form>
          ) : method === 'wechat' ? (
            <div className="mt-7 flex flex-1 flex-col">
              {joiningTeam ? (
                <div className="vnext-auth__qr mx-auto flex h-[230px] w-[230px] flex-col items-center justify-center rounded-[24px]" aria-hidden="true">
                  <Loader2 size={25} className="animate-spin" />
                </div>
              ) : !agreed ? (
                <button
                  type="button"
                  onClick={refreshWechatCode}
                  className="vnext-auth__qr mx-auto flex h-[230px] w-[230px] flex-col items-center justify-center rounded-[24px]"
                >
                  <span className="vnext-auth__qr-icon flex h-14 w-14 items-center justify-center rounded-[18px]">
                    <MessageCircle size={25} strokeWidth={1.7} />
                  </span>
                  <p className="mt-4 text-[13px] font-semibold">微信扫码登录</p>
                  <p className="vnext-auth__muted mt-1 px-5 text-[10px] leading-5">勾选协议后加载官方二维码</p>
                </button>
              ) : (
                <WechatQrFlow
                  createSession={createLoginSession}
                  pollSession={pollWechatLoginSession}
                  onAuthorized={onWechatAuthorized}
                  purpose="login"
                  darkActive={darkActive}
                />
              )}
            </div>
          ) : (
            <form onSubmit={verifyPhone} className="mt-7 flex flex-1 flex-col">
              <label className="vnext-auth__label text-[11px] font-medium" htmlFor="login-phone">手机号</label>
              <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                <span className="vnext-auth__prefix px-4 text-[12px]">+86</span>
                <input
                  id="login-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={cleanPhone}
                  onChange={event => setPhone(event.target.value)}
                  placeholder="请输入手机号"
                  className="vnext-auth__input h-full min-w-0 flex-1 bg-transparent px-4 text-[13px] outline-none"
                />
              </div>

              <label className="vnext-auth__label mt-5 text-[11px] font-medium" htmlFor="login-code">验证码</label>
              <div className="vnext-auth__field mt-2 flex h-12 items-center rounded-[13px] transition">
                <input
                  id="login-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 18))}
                  maxLength={18}
                  placeholder="请输入验证码"
                  className="vnext-auth__input h-full min-w-0 flex-1 bg-transparent px-4 text-[13px] outline-none"
                />
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={!phoneValid || sending || countdown > 0 || joiningTeam}
                  className="vnext-auth__send mr-2 rounded-[10px] px-3 py-2 text-[11px] font-medium transition disabled:cursor-not-allowed"
                >
                  {sending ? '发送中…' : countdown > 0 ? `${countdown}s` : '获取验证码'}
                </button>
              </div>

              <button
                type="submit"
                disabled={!phoneValid || !codeValid || verifying || joiningTeam}
                className="vnext-auth__submit mt-6 flex h-12 items-center justify-center gap-2 rounded-[13px] text-[13px] font-semibold transition disabled:cursor-not-allowed"
              >
                {verifying ? '正在登录' : '登录并继续'}
                {verifying ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
              </button>
            </form>
          )}

          {/* ⚠️ 提示必须放在两个表单**外面**。原来它只写在手机号表单里，
              邮箱登录连「邮箱或密码不正确」都一个字不显示——
              用户点了没反应，只能猜是自己错了还是系统坏了。 */}
          {errorMsg && (
            <p className="vnext-auth__error mt-3 text-[11px] leading-5" role="alert">{errorMsg}</p>
          )}
          {notice && (
            <p className="vnext-auth__notice mt-3 text-[11px] leading-5" role="status">{notice}</p>
          )}

          <label className={`vnext-auth__agreement mt-5 flex cursor-pointer items-start gap-2.5 text-[10px] leading-5${agreementHint ? ' is-hint' : ''}`}>
            <input
              type="checkbox"
              checked={agreed}
              disabled={joiningTeam}
              onChange={event => {
                setAgreed(event.target.checked);
                // 勾上了就把提示和高亮收掉——问题解决了还挂着提示很讨厌
                if (event.target.checked) { setAgreementHint(false); setNotice(current => (current === AGREEMENT_NOTICE ? '' : current)); }
              }}
              className="peer sr-only"
            />
            <span className="vnext-auth__checkbox mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] text-transparent transition peer-checked:border-[#a793bc] peer-checked:bg-[#8b779f] peer-checked:text-white">
              <Check size={10} strokeWidth={2.5} />
            </span>
            <span>
              我已阅读并同意
              <a href="/terms" className="vnext-auth__link mx-1">《用户协议》</a>
              和
              <a href="/privacy" className="vnext-auth__link ml-1">《隐私政策》</a>
            </span>
          </label>

            <div className="vnext-auth__security mt-6 flex items-center gap-2 border-t pt-5 text-[10px]">
            <ShieldCheck size={13} />
            登录仅用于同步你的项目与点数
          </div>
        </div>

        <aside className="vnext-auth__artwork relative hidden min-h-[680px] overflow-hidden lg:block" aria-label="原创创作视觉">
          <img src="/images/vnext/writing.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111014]/92 via-[#111014]/16 to-[#111014]/22" />
          <div className="absolute inset-x-0 bottom-0 p-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/44">From story to screen</p>
            <p className="mt-4 max-w-md text-[25px] font-medium leading-[1.35] tracking-[-0.035em] text-white/92">每一个画面，<br />都从一句故事开始。</p>
            <p className="mt-4 max-w-sm text-[12px] leading-6 text-white/48">写剧本、审故事、做分镜，把想法一步步变成可以交付的作品。</p>
          </div>
        </aside>
      </section>
    </main>
    </PublicTeamBrandProvider>
  );
}
